import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserRole } from 'src/app/features/admin/types/user.type';
import { DialogService } from 'src/app/core/services/dialog.service';
import {
  MAIN_NAVIGATION,
  MOBILE_ONLY_NAVIGATION,
  NavigationItem,
  NavigationSubmenu,
  NavigationSubmenuItem,
  PRIMARY_NAVIGATION,
  SECONDARY_NAVIGATION
} from './navigation.config';

@Component({
    selector: 'vee-navigation-header',
    templateUrl: './navigation-header.component.html',
    styleUrls: ['./navigation-header.component.scss'],
    standalone: false
})
export class NavigationHeaderComponent implements OnInit, OnDestroy {
  @Input() active!: string
  menuIsOpen = false;
  openSubmenuIndex: number | null = null; // Store the index of the currently open submenu
  color?: string
  isLoginPage = false;

  // Delay before a hover-opened submenu closes, so briefly crossing the gap
  // between the trigger and the dropdown (or a fast mouse pass-over) doesn't
  // instantly collapse it. Cleared on re-entry into either region.
  private static readonly SUBMENU_CLOSE_DELAY_MS = 150;
  private submenuCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;
  
  primaryNavigation = PRIMARY_NAVIGATION;
  secondaryNavigation = SECONDARY_NAVIGATION;
  mainNavigation = MAIN_NAVIGATION;
  mobileOnlyNavigation = MOBILE_ONLY_NAVIGATION;
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authservice: AuthService,
    public dialog: DialogService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild
          }
          return route
        }),
        mergeMap((route) => route.data),
        map((data) => {
          return data &&
            typeof data === 'object' &&
            Object.prototype.hasOwnProperty.call(data, 'color')
            ? (data as { color?: string })['color']
            : 'white';
        }),
        catchError(() => of('white')),
      )
      .subscribe((res) => {
        this.color = res
      })
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLoginPage = this.router.url.includes('/auth/login');
      }
    });
    document.addEventListener('click', this.handleOutsideClick)
  }

  @HostBinding('class')
  get styles() {
    if (this.color == 'cyan') return 'bg-cyan-lightest cyan'
    if (this.color == 'yellow') return 'bg-yellow-lighter yellow'
    return 'bg-white cyan'
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    this.clearScheduledSubmenuClose();
  }

  /* Closes on a click anywhere outside a submenu trigger/panel — other nav
     items, blank header space, page content.

     Checks the STATIC `.submenu-trigger` class, never the dynamic `.open`
     one. `.open` is an Angular `[class.open]` binding: change detection
     re-renders it after the zone task settles, not synchronously inside a
     native event's bubble phase. A click on the trigger to *reopen* it
     already flipped `openSubmenuIndex` via (click) by the time this
     document-level listener runs (same synchronous bubble), but the DOM's
     `.open` class hasn't caught up yet — so checking it here would read
     the stale pre-click state and immediately re-close what the click just
     opened. `.submenu-trigger` is static and never toggles, so it carries
     no such race; clicking a trigger is left entirely to toggleSubmenu(). */
  private handleOutsideClick = (event: Event) => {
    const target = event.target as Element | null;
    if (!target?.closest('li.submenu-trigger')) {
      this.closeSubmenu();
    }
  };

  // Listens on `document`, not the host element — the header and
  // <router-outlet> are siblings in app.component.html, so a plain
  // host-scoped listener would never see Escape presses while focus sits
  // on routed page content (the common case, since opening via hover never
  // moves focus into the header at all).
  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeSubmenu();
  }

  toggleSubmenu(index: number) {
    this.clearScheduledSubmenuClose();
    this.openSubmenuIndex = this.openSubmenuIndex === index ? null : index
  }

  closeSubmenu() {
    this.clearScheduledSubmenuClose();
    this.openSubmenuIndex = null;
  }

  // Hover only ever opens — closing is always routed through the delayed
  // path below so a fast mouse-over doesn't flicker the dropdown shut.
  openSubmenuOnHover(index: number) {
    this.clearScheduledSubmenuClose();
    this.openSubmenuIndex = index;
  }

  scheduleSubmenuClose() {
    this.clearScheduledSubmenuClose();
    this.submenuCloseTimeoutId = setTimeout(() => {
      this.submenuCloseTimeoutId = null;
      this.openSubmenuIndex = null;
      // The property update above is correct without this, but nothing else
      // marks the view dirty for a plain setTimeout callback — same gap as
      // ResultsComponent#loadResults() (see its own NG0100/timing comment).
      this.cdr.detectChanges();
    }, NavigationHeaderComponent.SUBMENU_CLOSE_DELAY_MS);
  }

  private clearScheduledSubmenuClose() {
    if (this.submenuCloseTimeoutId !== null) {
      clearTimeout(this.submenuCloseTimeoutId);
      this.submenuCloseTimeoutId = null;
    }
  }

  get isLoggedIn(): boolean {
    return this.authservice.tokenIsSet;
  }

  logout() {
    this.authservice.logout();
  }

  get isAdminOrContentCreator() {
    if(!this.authservice.user) return false;
    return this.authservice.matchingRoles([UserRole.Admin, UserRole.ContentCreator]);
  }
  
  get isAdminOrContentCreatorOrCounsellor() {
    if(!this.authservice.user) return false;
    return this.authservice.matchingRoles([UserRole.Admin, UserRole.ContentCreator, UserRole.Counsellor]);
  }

  // Type guards
  isNavigationItem(item: NavigationItem | NavigationSubmenu): item is NavigationItem {
    return !('items' in item);
  }

  isNavigationSubmenu(item: NavigationItem | NavigationSubmenu): item is NavigationSubmenu {
    return 'items' in item;
  }

  // Check if user has required roles
  private hasRequiredRoles(requiredRoles?: UserRole[]): boolean {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }
    
    if (!this.authservice.user) {
      return false;
    }

    return requiredRoles.some(role => 
      this.authservice.user!.roles.includes(role)
    );
  }

  // Check if user is authenticated
  private isAuthenticated(requiresAuth?: boolean): boolean {
    if (!requiresAuth) {
      return true;
    }
    return this.authservice.tokenIsSet;
  }

  shouldShowItem(item: NavigationItem | NavigationSubmenu): boolean {

    if (!this.isAuthenticated(item.requiresAuth)) {
      return false;
    }

    if ('hideIfAuth' in item && item.hideIfAuth && this.authservice.tokenIsSet) {
      return false;
    }

    if (!this.hasRequiredRoles(item.requiresRole)) {
      return false;
    }

    return true;
  }

  shouldShowSubmenuItem(item: NavigationSubmenuItem): boolean {
    return this.hasRequiredRoles(item.requiresRole);
  }

  isRouteActive(route: string): boolean {
    return this.router.url.includes(route);
  }

  handleNavigationAction(item: NavigationItem): void {
    if (item.id === 'language' || item.id === 'language-mobile') {
      this.dialog.open('languageSelectDialog');
    } else if (item.id === 'logout' || item.id === 'logout-mobile') {
      this.logout();
    }
  }
}
